import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { BbField } from '@/app/pages/create-brief-select/ui/bb-field/bb-field';
import { BbAiThoughtsScroll } from '@/app/pages/create-brief-select/ui/bb-ai-thoughts-scroll/bb-ai-thoughts-scroll';
import { CreateScreeningService } from '@/app/pages/screening-brief-create/services/create-screening/create-screening.service';
import { ScreeningBriefAiService } from '../../services/screening-brief-ai.service';
import { SCREEN_LOOKFOR_POINTERS, CUSTOM_RESTRICTION_CATEGORY_ID } from '../../screening-brief.constants';
import { htmlListToLines } from '../../screening-brief.utils';
import { BriefBasics } from '../brief-basics/brief-basics';

/**
 * Wizard step 2 — name the brief and describe the *requirements*: the positives only.
 *
 * The red flags deliberately live in step 3; the forward-pointer callout here is what sets that
 * expectation, so keep the two apart.
 */
@Component({
  selector: 'sbs-screening-define',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BbField, BbAiThoughtsScroll, BriefBasics],
  templateUrl: './screening-define.html',
})
export class ScreeningDefine {

  readonly createScreeningService = inject(CreateScreeningService);
  readonly screeningAi = inject(ScreeningBriefAiService);

  readonly pointers = SCREEN_LOOKFOR_POINTERS;

  readonly lookForCount = computed(() => this.createScreeningService.lookFor().trim().length);

  setLookFor(value: string): void {
    this.createScreeningService.lookFor.set(value);
  }

  /**
   * Streams an AI draft, then folds it into this step's shape: the requirements HTML becomes the
   * free-text `lookFor`, and each "don't" becomes a custom red flag so step 3 starts populated.
   */
  async draftWithAi(): Promise<void> {
    if (this.screeningAi.isDrafting()) {
      return;
    }
    const drafted = await this.screeningAi.draftScreeningBrief(
      'Draft a strong content-screening brief considering this product and brand.',
    );
    if (!drafted) {
      return;
    }
    this.applyDraftToWizard();
  }

  private applyDraftToWizard(): void {
    const service = this.createScreeningService;
    const requirements = htmlListToLines(service.requirementsHtml());
    if (requirements.length) {
      service.lookFor.set(requirements.join('\n'));
    }
    for (const line of htmlListToLines(service.restrictionsHtml())) {
      service.addRestriction(CUSTOM_RESTRICTION_CATEGORY_ID, CUSTOM_RESTRICTION_CATEGORY_ID, line, true);
    }
  }
}
